import React, { useState } from "react";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Clock, Loader2, Send } from "lucide-react";
import { Container, Reveal, Overline } from "../components/common";
import { PageHero } from "../components/PageHero";
import { submitContact, fetchSiteSettings } from "../lib/api";
import { useApiData } from "../hooks/useApiData";
import { CONTACT, IMAGES } from "../lib/content";

const inputCls =
  "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-navy placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-600/40 focus:border-teal-600 transition";

const mapContactSettings = (settings) => ({
  address: settings.address || CONTACT.address,
  person: settings.contact_person || CONTACT.person,
  phone: settings.phone || CONTACT.phone,
  email: settings.email || CONTACT.email,
});

export default function Contact() {
  const contact = useApiData(fetchSiteSettings, CONTACT, mapContactSettings);
  const INFO = [
    { icon: MapPin, label: "Visit Us", value: contact.address },
    { icon: Phone, label: "Call Us", value: `${contact.person} — ${contact.phone}`, href: `tel:${contact.phone}` },
    { icon: Mail, label: "Email Us", value: contact.email, href: `mailto:${contact.email}` },
    { icon: Clock, label: "Office Hours", value: "Mon – Sat · 9:30 AM to 6:00 PM" },
  ];
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "General Inquiry", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await submitContact(form);
      toast.success(data.message || "Your message has been sent!");
      setForm({ name: "", email: "", phone: "", subject: "General Inquiry", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageHero
        overline="Contact Us"
        title="We'd love to hear from you"
        subtitle="Questions, collaborations or just a hello — reach out and our team will get back to you."
        image={IMAGES.womenGroup}
      />

      <section className="py-20 lg:py-28 bg-white" data-testid="contact-section">
        <Container>
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
            <Reveal className="lg:col-span-5">
              <Overline>Reach Us</Overline>
              <h2 className="mt-4 font-display text-3xl sm:text-4xl font-extrabold text-navy leading-tight">
                Let's build a kinder tomorrow, together
              </h2>
              <div className="mt-8 space-y-4">
                {INFO.map((item) => (
                  <div key={item.label} className="flex items-start gap-4 rounded-2xl bg-offwhite border border-slate-100 p-5" data-testid={`contact-info-${item.label.toLowerCase().replace(/\s+/g, "-")}`}>
                    <span className="grid place-items-center h-11 w-11 shrink-0 rounded-xl bg-teal-50 text-teal-700">
                      <item.icon className="h-5 w-5" />
                    </span>
                    <div>
                      <div className="font-mono text-xs uppercase tracking-wider text-slate-400">{item.label}</div>
                      {item.href ? (
                        <a href={item.href} className="mt-1 block font-medium text-navy hover:text-teal-700 transition-colors">
                          {item.value}
                        </a>
                      ) : (
                        <div className="mt-1 font-medium text-navy leading-relaxed">{item.value}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>

            <Reveal delay={0.1} className="lg:col-span-7">
              <div className="rounded-3xl bg-offwhite border border-slate-100 p-7 sm:p-9 shadow-soft">
                <h3 className="font-display text-xl font-bold text-navy mb-6">Send us a message</h3>
                <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <label className="block">
                      <span className="block text-sm font-medium text-navy mb-1.5">Full Name *</span>
                      <input required className={inputCls} value={form.name} onChange={set("name")} data-testid="contact-name" placeholder="Your name" />
                    </label>
                    <label className="block">
                      <span className="block text-sm font-medium text-navy mb-1.5">Email *</span>
                      <input required type="email" className={inputCls} value={form.email} onChange={set("email")} data-testid="contact-email" placeholder="you@email.com" />
                    </label>
                    <label className="block">
                      <span className="block text-sm font-medium text-navy mb-1.5">Phone</span>
                      <input className={inputCls} value={form.phone} onChange={set("phone")} data-testid="contact-phone" placeholder="Mobile number" />
                    </label>
                    <label className="block">
                      <span className="block text-sm font-medium text-navy mb-1.5">Subject</span>
                      <input className={inputCls} value={form.subject} onChange={set("subject")} data-testid="contact-subject" placeholder="How can we help?" />
                    </label>
                  </div>
                  <label className="block">
                    <span className="block text-sm font-medium text-navy mb-1.5">Message *</span>
                    <textarea required rows={5} className={inputCls} value={form.message} onChange={set("message")} data-testid="contact-message" placeholder="Write your message here…" />
                  </label>
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="contact-submit"
                    className="inline-flex items-center gap-2 rounded-full bg-coral px-7 py-3.5 text-sm font-semibold text-white hover:bg-coral-700 transition-colors disabled:opacity-60"
                  >
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    Send Message
                  </button>
                </form>
              </div>
            </Reveal>
          </div>
        </Container>
      </section>
    </>
  );
}
